import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getWebSocketBaseUrl, joinQuizShowRoom } from "../../api";
import { useWebSocket } from "../../hooks/useWebSocket";
import MultiplayerPlayerView from "../../components/MultiplayerPlayerView";
import { parseQuizShowState } from "./quizShowShared";

const QuizShowJoin: React.FC = () => {
  const navigate = useNavigate();
  const { roomCode: roomCodeFromUrl } = useParams();
  const [roomCode, setRoomCode] = useState(
    (roomCodeFromUrl || "").toUpperCase(),
  );
  const [displayName, setDisplayName] = useState("");
  const [playerId, setPlayerId] = useState<string | null>(null);
  const [joinedName, setJoinedName] = useState("");
  const [joinedRoom, setJoinedRoom] = useState("");
  const [error, setError] = useState("");
  const [isJoining, setIsJoining] = useState(false);

  const wsUrl = useMemo(() => {
    if (!playerId || !joinedRoom) {
      return null;
    }
    return `${getWebSocketBaseUrl()}/ws/quiz-show/${joinedRoom}?role=player&token=${encodeURIComponent(playerId)}`;
  }, [playerId, joinedRoom]);

  const { send, lastMessage, isOpen } = useWebSocket(wsUrl);
  const state = parseQuizShowState(lastMessage);

  useEffect(() => {
    const storedCode = (roomCodeFromUrl || "").toUpperCase();
    if (!storedCode) {
      return;
    }
    try {
      const stored = sessionStorage.getItem(`quizShow:${storedCode}`);
      if (!stored) {
        return;
      }
      const parsed = JSON.parse(stored) as {
        playerId?: string;
        displayName?: string;
      };
      if (parsed.playerId && parsed.displayName) {
        setPlayerId(parsed.playerId);
        setJoinedName(parsed.displayName);
        setJoinedRoom(storedCode);
      }
    } catch {
      sessionStorage.removeItem(`quizShow:${storedCode}`);
    }
  }, [roomCodeFromUrl]);

  const handleJoin = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    setIsJoining(true);
    try {
      const result = await joinQuizShowRoom(
        roomCode.trim().toUpperCase(),
        displayName,
      );
      sessionStorage.setItem(
        `quizShow:${result.roomCode}`,
        JSON.stringify({
          playerId: result.playerId,
          displayName: result.displayName,
        }),
      );
      setPlayerId(result.playerId);
      setJoinedName(result.displayName);
      setJoinedRoom(result.roomCode);
    } catch (joinError) {
      setError(
        joinError instanceof Error ? joinError.message : "Could not join.",
      );
    } finally {
      setIsJoining(false);
    }
  };

  if (playerId && joinedRoom) {
    return (
      <MultiplayerPlayerView
        roomCode={joinedRoom}
        displayName={joinedName}
        playerId={playerId}
        state={state}
        isOpen={isOpen}
        send={send}
      />
    );
  }

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-md flex-col justify-center p-6 text-black">
      <h1 className="mb-2 text-3xl font-bold">Join Quiz Show</h1>
      <p className="mb-6 text-lg">Enter the room code from the class screen.</p>
      <form onSubmit={handleJoin} className="space-y-4">
        <label className="block">
          <span className="mb-1 block font-bold">Room code</span>
          <input
            value={roomCode}
            onChange={(event) => setRoomCode(event.target.value.toUpperCase())}
            className="w-full rounded border border-black p-3 text-2xl tracking-[0.3em]"
            autoCapitalize="characters"
            autoCorrect="off"
            required
          />
        </label>
        <label className="block">
          <span className="mb-1 block font-bold">Your name</span>
          <input
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            className="w-full rounded border border-black p-3 text-xl"
            maxLength={24}
            required
          />
        </label>
        {error && <p className="text-red-600">{error}</p>}
        <button
          type="submit"
          disabled={isJoining}
          className="w-full rounded border border-black bg-green-500 p-3 text-xl font-bold text-white hover:bg-green-600"
        >
          {isJoining ? "Joining..." : "Join"}
        </button>
      </form>
      <button
        type="button"
        onClick={() => navigate("/")}
        className="mt-6 text-center text-sm underline"
      >
        Back
      </button>
    </div>
  );
};

export default QuizShowJoin;
